/**
 * A simple cache for storing signed image URLs to avoid repeated requests
 */

interface CacheEntry {
  url: string;
  timestamp: number;
  expiresAt: number; // Timestamp when the URL expires
}

const SIGNATURE_EXPIRY_MARGIN_MS = 30000;

const signedUrlExpiry = (value: string): number | null => {
  try {
    const params = new URL(value).searchParams;
    if (params.has('X-Amz-Signature')) {
      const date = params.get('X-Amz-Date')?.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
      const duration = params.get('X-Amz-Expires') ?? '';
      if (!date || !/^\d+$/.test(duration)) return 0;
      const issuedAt = Date.parse(`${date[1]}-${date[2]}-${date[3]}T${date[4]}:${date[5]}:${date[6]}Z`);
      if (!Number.isFinite(issuedAt) || new Date(issuedAt).toISOString().replace(/[-:]|\.\d{3}/g, '') !== date[0]) return 0;
      const expiresAt = issuedAt + Number(duration) * 1000;
      return Number.isFinite(expiresAt) ? expiresAt : 0;
    }
    if (params.has('Signature')) {
      const expires = params.get('Expires') ?? '';
      if (!/^\d+$/.test(expires)) return 0;
      const expiresAt = Number(expires) * 1000;
      return Number.isFinite(expiresAt) ? expiresAt : 0;
    }
  } catch {
    // Non-URL cache entries keep the caller's existing lifetime.
  }
  return null;
};

export const isSignedImageUrlUsable = (value: string): boolean => {
  const expiry = signedUrlExpiry(value);
  return expiry !== null && expiry - SIGNATURE_EXPIRY_MARGIN_MS > Date.now();
};

class ImageCache {
  private cache: Record<string, CacheEntry> = {};
  private readonly DEFAULT_EXPIRY_MS = 3600000; // 1 hour default expiry

  /**
   * Set a signed URL in the cache with expiration
   */
  set(key: string, url: string, expiryMs = this.DEFAULT_EXPIRY_MS): void {
    const now = Date.now();
    const signatureExpiry = signedUrlExpiry(url);
    const expiresAt = Math.min(
      now + expiryMs,
      signatureExpiry === null ? Infinity : signatureExpiry - SIGNATURE_EXPIRY_MARGIN_MS,
    );
    if (expiresAt <= now) {
      delete this.cache[key];
      return;
    }
    this.cache[key] = {
      url,
      timestamp: now,
      expiresAt,
    };
  }

  /**
   * Get a signed URL from the cache if it exists and hasn't expired
   */
  get(key: string): string | null {
    const entry = this.cache[key];
    if (!entry) return null;

    // Check if the entry has expired
    if (Date.now() >= entry.expiresAt) {
      delete this.cache[key];
      return null;
    }

    return entry.url;
  }

  /**
   * Clear the entire cache or a specific key
   */
  clear(key?: string): void {
    if (key) {
      delete this.cache[key];
    } else {
      this.cache = {};
    }
  }

  /**
   * Check if a key exists in the cache and is valid
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }
}

// Export a singleton instance
export const imageCache = new ImageCache();
